import React, { useState, useRef, useEffect } from 'react';

/**
 * Multi-select filter.
 *
 * Default: Redfin-style trigger with an absolutely positioned popover menu.
 * `inline`: renders as an accordion section (menu in normal flow, no
 * click-outside dismissal) for use inside the mobile filters sheet.
 */
function FilterDropdown({ label, options, selected, onChange, inline = false }) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside (popover mode only)
  useEffect(() => {
    if (inline) return undefined;
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [inline]);

  // Handle option toggle
  const handleToggle = (option) => {
    const newSelected = selected.includes(option)
      ? selected.filter((item) => item !== option)
      : [...selected, option];
    onChange(newSelected);
  };

  // Clear all selections
  const handleClear = () => {
    onChange([]);
    if (!inline) setIsOpen(false);
  };

  const hasSelection = selected.length > 0;

  return (
    <div className={`filter-dropdown ${inline ? 'filter-dropdown--inline' : ''}`} ref={dropdownRef}>
      <button
        className={`filter-dropdown-trigger ${isOpen ? 'active' : ''} ${hasSelection ? 'has-selection' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <span>{label}</span>
        {hasSelection && (
          <span className="filter-dropdown-badge">{selected.length}</span>
        )}
        <svg
          className="filter-dropdown-chevron"
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
        >
          <path
            d="M3 4.5L6 7.5L9 4.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      <div className={`filter-dropdown-menu ${isOpen ? 'open' : ''}`} role="listbox">
        {options.map((option) => {
          const isSelected = selected.includes(option);
          return (
            <div
              key={option}
              className={`filter-dropdown-option ${isSelected ? 'selected' : ''}`}
              role="option"
              aria-selected={isSelected}
              onClick={() => handleToggle(option)}
            >
              <span className="filter-dropdown-checkbox">
                <svg
                  className="filter-dropdown-checkmark"
                  viewBox="0 0 12 12"
                  fill="none"
                >
                  <path
                    d="M2.5 6L5 8.5L9.5 4"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <span className="filter-dropdown-label">{option}</span>
            </div>
          );
        })}

        {hasSelection && (
          <button className="filter-dropdown-clear" onClick={handleClear}>
            Clear All
          </button>
        )}
      </div>
    </div>
  );
}

export default FilterDropdown;

